import { gql } from '@apollo/client'

export const UPDATE_SETTINGS = gql`
  mutation updateSettings($merchant_id: uuid!, $id: uuid!, $settings: jsonb!) {
    update_stores(
      where: { merchant_id: { _eq: $merchant_id }, id: { _eq: $id } }
      _append: { settings: $settings }
    ) {
      affected_rows
      returning {
        settings
      }
    }
  }
`

export const UPDATE_SETTINGS_AND_SAMEDAY_PICKUP_MOV = gql`
  mutation updateSettings(
    $merchant_id: uuid!
    $id: uuid!
    $settings: jsonb!
    $pickup_sameday_mov: numeric!
  ) {
    update_stores(
      where: { merchant_id: { _eq: $merchant_id }, id: { _eq: $id } }
      _append: { settings: $settings }
      _set: { sameday_pickup_minimum_order_value: $pickup_sameday_mov }
    ) {
      affected_rows
      returning {
        settings
      }
    }
  }
`

export const UPDATE_SETTINGS_AND_MINIMUM_ORDER_VALUE = gql`
  mutation updateSettings(
    $merchant_id: uuid!
    $id: uuid!
    $settings: jsonb!
    $sameday_delivery_minimum_order_value: numeric
  ) {
    update_stores(
      where: { merchant_id: { _eq: $merchant_id }, id: { _eq: $id } }
      _append: { settings: $settings }
      _set: {
        sameday_delivery_minimum_order_value: $sameday_delivery_minimum_order_value
      }
    ) {
      affected_rows
      returning {
        settings
      }
    }
  }
`

export const RECALCULATE_STORE_TIMER = gql`
  mutation recalculateStoreTimer($storeId: String!) {
    updateStoreTimer(storeId: $storeId) {
      statusChangeIn
    }
  }
`

export const UPDATE_STORE_PREPARATION_TIME = gql`
  mutation updateStorePreparationTime(
    $merchant_id: uuid!
    $id: uuid!
    $settings: jsonb!
  ) {
    update_stores(
      where: { merchant_id: { _eq: $merchant_id }, id: { _eq: $id } }
      _append: { settings: $settings }
    ) {
      affected_rows
      returning {
        settings
      }
    }
  }
`

export const UPDATE_STORE = gql`
  mutation updateStore(
    $store_id: uuid!
    $name: String!
    $slug: String!
    $email: String!
    $pickup_notes: String
    $line_1: String!
    $line_2: String!
    $city: String!
    $country: String!
    $zip: String!
    $geom: geometry!
    $contact_num: String!
    $archived_at: timestamp
    $address_cache: jsonb
    $address_is_new_version: Boolean = true
    $is_open: Boolean!
  ) {
    update_stores(
      where: { id: { _eq: $store_id } }
      _set: {
        name: $name
        slug: $slug
        email: $email
        pickup_notes: $pickup_notes
        archived_at: $archived_at
        address_cache: $address_cache
        is_open: $is_open
      }
    ) {
      affected_rows
      returning {
        id
      }
    }
    update_addresses(
      where: { store_id: { _eq: $store_id } }
      _set: {
        line_1: $line_1
        line_2: $line_2
        city: $city
        country: $country
        zip: $zip
        geom: $geom
        contact_num: $contact_num
        is_new_version: $address_is_new_version
        updated_at: "now()"
      }
    ) {
      affected_rows
      returning {
        id
      }
    }
  }
`

export const CREATE_LOCATION = gql`
  mutation createLocation(
    $merchant_id: ID!
    $name: String!
    $email: String!
    $pickup_notes: String
    $settings: SettingsParams!
    $prep_reminders: PrepRemindersParams!
    $variant_data: [VariantDataParams]!
    $store_modifiers: [StoreModifierParams]!
    $users: [StoreUserParams]!
    $address: AddressParams!
  ) {
    createStore(
      merchantId: $merchant_id
      name: $name
      email: $email
      pickupNotes: $pickup_notes
      settings: $settings
      prepReminders: $prep_reminders
      variantData: $variant_data
      storeModifiers: $store_modifiers
      address: $address
      users: $users
    ) {
      id
    }
  }
`

export const TOGGLE_PREORDER = gql`
  mutation toggleWarehouseStatus($slug: String) {
    toggleWarehouseStatus(slug: $slug) {
      id
    }
  }
`

export const OPEN_CLOSE_STORE = gql`
  mutation openCloseStore($userId: ID!, $storeId: ID!, $isOpen: Boolean!) {
    openOrCloseStore(userId: $userId, storeId: $storeId, isOpen: $isOpen) {
      is_open
    }
  }
`

export const UPDATE_STORE_VARIANT_STOCK_STATUS = gql`
  mutation toggleStockStatus($store_variant_id: uuid!, $in_stock: Boolean!) {
    update_store_variants(
      where: { id: { _eq: $store_variant_id } }
      _set: { in_stock: $in_stock }
    ) {
      affected_rows
      returning {
        id
        in_stock
      }
    }
  }
`

export const UPDATE_STORE_MODIFIER_STOCK_STATUS = gql`
  mutation toggleStockStatus($store_modifier_id: uuid!, $in_stock: Boolean!) {
    update_store_modifiers(
      where: { id: { _eq: $store_modifier_id } }
      _set: { in_stock: $in_stock }
    ) {
      affected_rows
      returning {
        id
        in_stock
      }
    }
  }
`

export const PUBLISH_STORE_VARIANT = gql`
  mutation PublishStoreVariant($store_variant_id: uuid!) {
    update_store_variants(
      where: { id: { _eq: $store_variant_id } }
      _set: { published_at: "now()" }
    ) {
      affected_rows
      returning {
        id
        published_at
      }
    }
  }
`

export const UNPUBLISH_STORE_VARIANT = gql`
  mutation UnpublishStoreVariant($store_variant_id: uuid!) {
    update_store_variants(
      where: { id: { _eq: $store_variant_id } }
      _set: { published_at: null }
    ) {
      affected_rows
      returning {
        id
        published_at
      }
    }
  }
`

export const PUBLISH_STORE_MODIFIER = gql`
  mutation PublishStoreModifier($store_modifier_id: uuid!) {
    update_store_modifiers(
      where: { id: { _eq: $store_modifier_id } }
      _set: { published_at: "now()" }
    ) {
      affected_rows
      returning {
        id
        published_at
      }
    }
  }
`

export const UNPUBLISH_STORE_MODIFIER = gql`
  mutation UnpublishStoreModifier($store_modifier_id: uuid!) {
    update_store_modifiers(
      where: { id: { _eq: $store_modifier_id } }
      _set: { published_at: null }
    ) {
      affected_rows
      returning {
        id
        published_at
      }
    }
  }
`

export const CREATE_STORE_USERS = gql`
  mutation createStoreUsers($store_id: String!, $users: [StoreUserParams!]!) {
    createStoreUsers(storeId: $store_id, users: $users) {
      id
    }
  }
`

export const CREATE_PRE_ORDER_SETTINGS = gql`
  mutation createPreOrderSettings(
    $id: uuid!
    $store_id: uuid!
    $cutoffs: jsonb
  ) {
    insert_pre_order_settings_one(
      object: {
        id: $id
        store_id: $store_id
        cutoffs: $cutoffs
        delivery_enabled: true
        delivery_interval: 30
        delivery_times: "opening_hours"
        delivery_radius: 2.0
        pickup_enabled: true
        pickup_times: "opening_hours"
        pickup_interval: 15
        delivery_courier_partner: true
        pre_order_rate_cards_enabled: true
        pre_order_day_allowance: 30
      }
    ) {
      id
    }

    update_stores(
      where: { id: { _eq: $store_id } }
      _set: { preorder_pickup_minimum_order_value: 0 }
    ) {
      affected_rows
    }
  }
`

export const STORE_SLUG_CHANGE_LOG = gql`
  mutation storeSlugChange(
    $storeId: ID!
    $oldSlug: String!
    $newSlug: String!
  ) {
    storeSlugChange(storeId: $storeId, oldSlug: $oldSlug, newSlug: $newSlug)
  }
`
